// /simulations/new container (simulations.md): the Authoring-archetype
// full-spec launch route. Owns the option reads (versions, connections,
// personas, scenario sets, templates — all agent-filtered), the ?from=
// duplicate prefill (a past batch's authored spec), the DEBOUNCED
// `POST /v1/batches:preview` that feeds the receipt panel, and the launch
// POST. `?template=` loads the saved spec into that identical path. Launch
// returns to /simulations?launched=<id> where the new batch is highlighted;
// results open from there in Transcripts.
import { useCallback, useEffect, useRef, useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { components } from "../api/schema";
import { apiClient } from "../lib/apiClient";
import { defaultBatchDisplayName } from "../lib/batch";
import { ApiRequestError, requestErrorDescription } from "../lib/errors";
import { createIdempotencyKey } from "../lib/idempotency";
import { queryKeys } from "../lib/queryKeys";
import {
  parseSimulationCreateSearch,
  type SimulationCreateSearch,
} from "../lib/routerSearch";
import { useTypedNavigate, useTypedSearch } from "../lib/typedRouter";
import {
  useWorkingScope,
  withWorkingScopeSearch,
  type WorkingScopeSearch,
} from "../lib/workingScope";
import { Button, EmptyState, Page, Skeleton, useToast } from "../ui";
import { AgentScopePicker } from "./AgentScopeSelect";
import { SimulationLaunchForm } from "./simulations/SimulationLaunchForm";
import {
  initialLaunchState,
  isTemplateSaveable,
  launchStateFromSpec,
  toCreateRequest,
  toTemplateSpec,
  type SimulationLaunchState,
} from "./simulations/simulationLaunchState";
import { useLaunchOptions } from "./simulations/useLaunchOptions";
import { useBatchSpecPreview } from "./simulations/useBatchSpecPreview";

type SimulationCreateSearchState = SimulationCreateSearch & WorkingScopeSearch;

export function SimulationCreate() {
  const { agentId } = useWorkingScope();
  const navigate = useTypedNavigate<SimulationCreateSearchState>();
  const search = useTypedSearch(
    withWorkingScopeSearch(parseSimulationCreateSearch),
  );

  // Agent scope belongs to the shell selector. An un-scoped arrival keeps the
  // body picker as the recovery state, including the zero-agent create CTA.
  return (
    <Page variant={agentId === undefined ? "config" : "workspace"}>
      {agentId === undefined ? (
        <AgentScopePicker
          emptyState={
            <EmptyState
              action={
                <Button
                  onClick={() => {
                    void navigate({ search: true, to: "/agents" });
                  }}
                >
                  Create agent
                </Button>
              }
              description="Simulations run against an agent."
              title="No agents"
            />
          }
          surfaceIcon="play"
          surfaceLabel="Simulations"
        />
      ) : null}
      {agentId === undefined ? null : (
        <SimulationCreateForAgent
          agentId={agentId}
          fromBatchId={search.from}
          templateId={search.template}
          // Remount on scope/prefill change: form state must never leak across
          // agents or duplicate subjects — every option is an agent-scoped id.
          key={`${agentId}:${search.template ?? search.from ?? "blank"}`}
        />
      )}
    </Page>
  );
}

interface SimulationCreateForAgentProps {
  agentId: string;
  fromBatchId?: string;
  templateId?: string;
}

function SimulationCreateForAgent({
  agentId,
  fromBatchId,
  templateId,
}: SimulationCreateForAgentProps) {
  const navigate = useTypedNavigate<SimulationCreateSearchState>();
  const queryClient = useQueryClient();
  const toast = useToast();

  // The option reads are shared with Template authoring (docs/14 §5: one
  // launch spec, one set of choices) — see `useLaunchOptions`.
  const launchOptions = useLaunchOptions(agentId);
  const variables = launchOptions.variables;
  const [state, setState] = useState<SimulationLaunchState>(() =>
    initialLaunchState(defaultBatchDisplayName()),
  );
  const [selectedTemplateId, setSelectedTemplateId] = useState(
    templateId ?? "",
  );

  // A blank ad-hoc launch starts from the current agent's newest version and
  // longest-lived connection. Template and duplicate subjects own their
  // saved choices instead, and a user selection always wins this one-time
  // initialization.
  const hasAppliedDefaults = useRef(false);
  useEffect(() => {
    if (
      hasAppliedDefaults.current ||
      launchOptions.isPending ||
      fromBatchId !== undefined ||
      templateId !== undefined
    ) {
      return;
    }
    hasAppliedDefaults.current = true;
    setState((current) => ({
      ...current,
      agentVersionId:
        current.agentVersionId ||
        launchOptions.options.versionOptions[0]?.id ||
        "",
      connectionId:
        current.connectionId ||
        launchOptions.options.connectionOptions[0]?.id ||
        "",
    }));
  }, [
    fromBatchId,
    launchOptions.isPending,
    launchOptions.options.connectionOptions,
    launchOptions.options.versionOptions,
    templateId,
  ]);

  // Duplicate-into-form: the source batch's authored spec prefills the state.
  const sourceBatch = useQuery({
    enabled: fromBatchId !== undefined,
    queryKey: queryKeys.batches.detail(fromBatchId ?? ""),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/batches/{id}", {
        params: { path: { id: fromBatchId ?? "" } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });
  const sourceTemplate = useQuery({
    enabled: selectedTemplateId.length > 0,
    queryKey: queryKeys.templates.detail(selectedTemplateId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/templates/{id}", {
        params: { path: { id: selectedTemplateId } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });

  const hydratedTemplateId = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (
      launchOptions.isPending ||
      sourceTemplate.data === undefined ||
      hydratedTemplateId.current === sourceTemplate.data.id
    ) {
      return;
    }
    hydratedTemplateId.current = sourceTemplate.data.id;
    const next = launchStateFromSpec(
      sourceTemplate.data.spec ?? null,
      state.displayName,
      variables,
    );
    next.templateId = sourceTemplate.data.id;
    setState(next);
  }, [
    launchOptions.isPending,
    sourceTemplate.data,
    state.displayName,
    variables,
  ]);

  const hydratedBatchId = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (
      launchOptions.isPending ||
      sourceBatch.data === undefined ||
      selectedTemplateId.length > 0 ||
      hydratedBatchId.current === sourceBatch.data.id
    ) {
      return;
    }
    hydratedBatchId.current = sourceBatch.data.id;
    setState(
      launchStateFromSpec(
        sourceBatch.data.spec ?? null,
        defaultBatchDisplayName(),
        variables,
      ),
    );
  }, [
    launchOptions.isPending,
    selectedTemplateId,
    sourceBatch.data,
    variables,
  ]);

  const preview = useBatchSpecPreview(
    state,
    variables,
    !launchOptions.isPending &&
      !launchOptions.isRefreshingVariables &&
      launchOptions.errorMessage === undefined,
  );

  const launchBatch = useMutation({
    mutationFn: async () => {
      const { data, error } = await apiClient.POST("/v1/batches", {
        body: toCreateRequest(state, variables),
        params: {
          header: { "Idempotency-Key": createIdempotencyKey() },
        },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    onError: (error: unknown) => {
      toast.error(requestErrorDescription(error, "Launch failed."));
    },
    onSuccess: async (batch) => {
      toast.success("Simulation launched.", {
        description:
          "Conversations are starting; results land in Transcripts as they finish.",
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.batches.all,
      });
      void navigate({
        search: ({ agent, team }: WorkingScopeSearch) => ({
          agent,
          launched: batch.id,
          team,
        }),
        to: "/simulations",
      });
    },
  });

  const returnToQueue = useCallback(() => {
    void navigate({
      search: ({ agent, team }: WorkingScopeSearch) => ({ agent, team }),
      to: "/simulations",
    });
  }, [navigate]);

  useEffect(() => {
    const returnOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented) {
        returnToQueue();
      }
    };
    window.addEventListener("keydown", returnOnEscape);
    return () => {
      window.removeEventListener("keydown", returnOnEscape);
    };
  }, [returnToQueue]);

  const saveTemplate = useMutation({
    mutationFn: async () => {
      const body: components["schemas"]["TemplateCreate"] = {
        agent_id: agentId,
        display_name: state.displayName.trim(),
        schedule: null,
        schedule_timezone: "UTC",
        spec: toTemplateSpec(state, variables),
      };
      const { data, error } = await apiClient.POST("/v1/templates", { body });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    onError: (error: unknown) => {
      toast.error("Template save failed", {
        description: requestErrorDescription(error),
      });
    },
    onSuccess: async (template) => {
      queryClient.setQueryData(
        queryKeys.templates.detail(template.id),
        template,
      );
      toast.success("Template saved");
      await queryClient.invalidateQueries({
        queryKey: queryKeys.templates.all,
      });
      setSelectedTemplateId(template.id);
      setState((current) => ({ ...current, templateId: template.id }));
    },
  });

  const optionsPending =
    launchOptions.isPending ||
    (fromBatchId !== undefined && sourceBatch.isPending) ||
    (selectedTemplateId.length > 0 && sourceTemplate.isPending);

  const optionsError =
    launchOptions.errorMessage ??
    (sourceTemplate.isError
      ? requestErrorDescription(sourceTemplate.error)
      : undefined) ??
    (sourceBatch.isError
      ? requestErrorDescription(sourceBatch.error)
      : undefined);

  // The route container owns `Page` + the header (UI-LAW §2.11: page identity
  // is the breadcrumb, never an in-page title).
  return (
    <>
      {optionsPending ? (
        <Skeleton
          label="Loading launch options"
          rows={8}
          shape="detail-field"
        />
      ) : (
        <SimulationLaunchForm
          agentId={agentId}
          connectionOptions={launchOptions.options.connectionOptions}
          errorMessage={
            optionsError ??
            (launchBatch.isError
              ? requestErrorDescription(launchBatch.error, "Launch failed.")
              : undefined)
          }
          isLaunching={launchBatch.isPending}
          isPreviewPending={
            preview.isPending || launchOptions.isRefreshingVariables
          }
          onRetryOptions={
            launchOptions.errorMessage ? launchOptions.retry : undefined
          }
          onChange={setState}
          onLaunch={() => {
            launchBatch.mutate();
          }}
          onSaveAsTemplate={() => {
            saveTemplate.mutate();
          }}
          onTemplateSelect={setSelectedTemplateId}
          personaOptions={launchOptions.options.personaOptions}
          preview={preview.preview}
          previewErrorMessage={preview.errorMessage}
          onRetryPreview={preview.retry}
          scenarioOptions={launchOptions.options.scenarioOptions}
          scenarioSetOptions={launchOptions.options.scenarioSetOptions}
          state={state}
          templateOptions={launchOptions.options.templateOptions}
          variables={variables}
          versionOptions={launchOptions.options.versionOptions}
          isSavingTemplate={saveTemplate.isPending}
          isTemplateSaveable={isTemplateSaveable(state)}
        />
      )}
    </>
  );
}
